import { FormSection } from "Components/Shared/FormSection";
import { DateFormInput } from "Components/Shared/Inputs/Form/DateFormInput";
import { Dropdown } from "Components/Shared/Inputs/Form/Dropdown";
import { IdentificationCardDropdown } from "Components/Shared/Inputs/Form/Dropdowns/IdentificationCardDropdown";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { PhoneInput } from "Components/Shared/Inputs/Form/PhoneInput";
import { TextInput } from "Components/Shared/Inputs/TextInput";
import { CountryDropdown } from "Components/Shared/Inputs/Form/Dropdowns/CountryDropdown";
import { AddressFields } from "Components/Users/UserDetailPageContent/Components/AddressFields";
import type { PartyFormFieldsProps } from "Components/Users/UserDetailPageContent/Components/PartyForm/types";
import { useWatch } from "react-hook-form";
import { useGenderList } from "Hooks/Options/useGenderList";
import { usePartyTypeList } from "Hooks/Options/usePartyTypeList";
import { useCheckPermission } from "Hooks/usePermission";
import { Permission } from "Infrastructure/Api/Api";
import { AccessRightCodes } from "Models/AccessRightCodes";
import { Resources, useResource } from "Translations/Resources";
import { formatPhone } from "Utils/PhoneUtils";

const COMPONENT_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.Party.Form.NaturalPerson;

const IDENTIFICATION_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.Party.Form
    .PartyIdentification;

export const NaturalPersonFields = ({
  control,
  addresses,
  additionalIdentificationsSlot,
  verifiedPhone,
}: PartyFormFieldsProps) => {
  const { t } = useResource();
  const secondIdentification = useWatch({
    control,
    name: "partyIdentification.secondIdentification",
  });
  const nationality = useWatch({ control, name: "nationality" });
  const genderList = useGenderList();
  const partyTypeList = usePartyTypeList({ onlyBusiness: false });
  const canSeeDocuments = useCheckPermission(
    AccessRightCodes.IDDocuments,
    Permission.Read,
  );

  return (
    <>
      <Dropdown
        codeList={partyTypeList}
        name="type"
        control={control}
        label={t(COMPONENT_RESOURCES.Type)}
        disabled
      />
      <FormSection legend={t(COMPONENT_RESOURCES.Legend.PersonalData)}>
        <FormInput
          control={control}
          name="firstName"
          label={t(COMPONENT_RESOURCES.FirstName)}
        />
        <FormInput
          control={control}
          name="lastName"
          label={t(COMPONENT_RESOURCES.LastName)}
        />
        <FormInput
          control={control}
          name="personalNumber"
          label={t(COMPONENT_RESOURCES.PersonalNumber)}
          mask="000000/0000"
        />
        <DateFormInput
          control={control}
          name="birthDate"
          label={t(COMPONENT_RESOURCES.BirthDate)}
        />
        <Dropdown
          control={control}
          name="gender"
          label={t(COMPONENT_RESOURCES.Gender)}
          codeList={genderList}
        />
        <FormInput
          control={control}
          name="birthPlace"
          label={t(COMPONENT_RESOURCES.BirthPlace)}
        />
        <CountryDropdown
          control={control}
          name="nationality"
          label={t(Resources.Common.Nationality)}
        />
        {/* The party holds a list; the form offers one further citizenship, the first slot. */}
        <CountryDropdown
          control={control}
          name="otherNationalities.0"
          label={t(Resources.Common.OtherNationalities)}
          excludedCode={nationality}
        />
      </FormSection>
      <FormSection legend={t(COMPONENT_RESOURCES.Legend.Contact)}>
        <PhoneInput
          control={control}
          name="phone"
          label={t(COMPONENT_RESOURCES.Phone)}
        />
        <TextInput
          label={t(COMPONENT_RESOURCES.VerifiedPhone)}
          value={formatPhone(verifiedPhone)}
          disabled
        />
        <FormInput
          control={control}
          name="email"
          label={t(COMPONENT_RESOURCES.Email)}
          type="email"
          valueTransformer={(v) => v.toLowerCase()}
        />
      </FormSection>
      {canSeeDocuments && (
        <FormSection legend={t(COMPONENT_RESOURCES.Legend.Documents)}>
          <IdentificationCardDropdown
            control={control}
            name="partyIdentification.identification"
            label={t(IDENTIFICATION_RESOURCES.Identification)}
          />
          <FormInput
            control={control}
            name="partyIdentification.identificationNumber"
            label={t(IDENTIFICATION_RESOURCES.IdentificationNumber)}
          />
          <DateFormInput
            control={control}
            name="partyIdentification.identificationValidFrom"
            label={t(IDENTIFICATION_RESOURCES.IdentificationValidFrom)}
          />
          <DateFormInput
            control={control}
            name="partyIdentification.identificationValidTo"
            label={t(IDENTIFICATION_RESOURCES.IdentificationValidTo)}
          />
          <FormInput
            control={control}
            name="partyIdentification.identificationIssuer"
            label={t(IDENTIFICATION_RESOURCES.IdentificationIssuer)}
          />
          {secondIdentification && (
            <>
              <IdentificationCardDropdown
                control={control}
                name="partyIdentification.secondIdentification"
                label={t(IDENTIFICATION_RESOURCES.SecondDocumentTitle)}
              />
              <FormInput
                control={control}
                name="partyIdentification.secondIdentificationNumber"
                label={t(IDENTIFICATION_RESOURCES.IdentificationNumber)}
              />
              <DateFormInput
                control={control}
                name="partyIdentification.secondIdentificationValidFrom"
                label={t(IDENTIFICATION_RESOURCES.IdentificationValidFrom)}
              />
              <DateFormInput
                control={control}
                name="partyIdentification.secondIdentificationValidTo"
                label={t(IDENTIFICATION_RESOURCES.IdentificationValidTo)}
              />
              <FormInput
                control={control}
                name="partyIdentification.secondIdentificationIssuer"
                label={t(IDENTIFICATION_RESOURCES.IdentificationIssuer)}
              />
            </>
          )}
          {additionalIdentificationsSlot}
        </FormSection>
      )}
      <AddressFields addresses={addresses} control={control} />
    </>
  );
};
